import React, { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ImageCropModal } from '@/components/common/ImageCropModal'
import { brandsApi, Brand } from '@/lib/api/brands'
import { convertImageToWebP } from '@/lib/image-utils'
import { Upload, Image as ImageIcon, ArrowLeft, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

interface BrandCreateStepProps {
  onCreated: (brand: Brand) => void
  onBack?: () => void
}

const BrandCreateStep: React.FC<BrandCreateStepProps> = ({ onCreated, onBack }) => {
  const [brandName, setBrandName] = useState('')
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string>('')
  const [cropModalOpen, setCropModalOpen] = useState(false)
  const [cropImageUrl, setCropImageUrl] = useState<string>('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Please select an image smaller than 5MB')
      return
    }

    setSelectedFile(file)
    const previewUrl = URL.createObjectURL(file)
    setCropImageUrl(previewUrl)
    setCropModalOpen(true)
    e.target.value = ''
  }

  const handleCropSave = async (croppedBlob: Blob) => {
    const fileName = selectedFile?.name || 'brand-logo.webp'
    const file = new File([croppedBlob], fileName, { type: 'image/webp' })
    setLogoFile(file)

    const reader = new FileReader()
    reader.onload = (e) => setLogoPreview(e.target?.result as string)
    reader.readAsDataURL(file)

    setCropModalOpen(false)
    setSelectedFile(null)
    if (cropImageUrl) {
      URL.revokeObjectURL(cropImageUrl)
      setCropImageUrl('')
    }
  }

  const handleCropCancel = () => {
    setCropModalOpen(false)
    setSelectedFile(null)
    if (cropImageUrl) {
      URL.revokeObjectURL(cropImageUrl)
      setCropImageUrl('')
    }
  }

  const handleSubmit = async () => {
    if (!brandName.trim()) {
      toast.error('Brand name is required')
      return
    }

    setIsSubmitting(true)
    try {
      let logoUrl: string | undefined

      if (logoFile) {
        const { blob, fileName } = await convertImageToWebP(logoFile)
        const uploadRes = await brandsApi.getUploadUrl(fileName, 'image/webp')
        const { uploadUrl, publicUrl } = uploadRes.data
        const webpFile = new File([blob], fileName, { type: 'image/webp' })
        await brandsApi.uploadFile(uploadUrl, webpFile)
        logoUrl = publicUrl
      }

      const res = await brandsApi.createBrand({ name: brandName.trim(), logo: logoUrl })
      onCreated(res.data)
    } catch (err) {
      console.error('Failed to create brand:', err)
      toast.error('Failed to create brand. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-bold text-foreground">Create Your Brand</h2>
        <p className="text-gray-600 text-sm mt-1">
          Athletes will see this when reviewing your collaboration.
        </p>
      </div>

      <div className="space-y-5">
        {/* Logo upload */}
        <div className="flex flex-col items-center gap-3">
          <div
            className="w-32 h-32 rounded-lg border-2 border-dashed border-foreground/20 flex items-center justify-center overflow-hidden cursor-pointer hover:border-foreground/40 transition-colors bg-card"
            onClick={() => fileInputRef.current?.click()}
          >
            {logoPreview ? (
              <img src={logoPreview} alt="Brand logo" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center text-gray-500">
                <ImageIcon className="h-8 w-8 mx-auto mb-1" />
                <span className="text-xs">Logo</span>
              </div>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="bg-card border-foreground/10 text-foreground hover:bg-foreground/10"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-3.5 w-3.5 mr-1.5" />
            {logoPreview ? 'Change Logo' : 'Upload Logo'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        {/* Brand name */}
        <div className="space-y-2">
          <Label className="text-foreground">Brand Name</Label>
          <Input
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
            placeholder="e.g. Acme Corp"
            className="bg-card border-foreground/10 text-foreground placeholder:text-gray-500"
          />
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        {onBack && (
          <Button
            type="button"
            variant="outline"
            className="bg-transparent border-foreground/10 text-foreground hover:bg-foreground/5"
            onClick={onBack}
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back
          </Button>
        )}
        <Button
          className="flex-1 bg-[#C0FD72] hover:bg-[#b0ef62] text-black font-semibold"
          onClick={handleSubmit}
          disabled={isSubmitting || !brandName.trim()}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating Brand...
            </>
          ) : (
            'Create Brand & Continue'
          )}
        </Button>
      </div>

      {selectedFile && cropImageUrl && (
        <ImageCropModal
          open={cropModalOpen}
          onClose={handleCropCancel}
          onCropComplete={handleCropSave}
          imageUrl={cropImageUrl}
          title="Crop Brand Logo"
          aspect={1}
          cropShape="rect"
        />
      )}
    </div>
  )
}

export default BrandCreateStep
